import { baseApi, storefrontPath } from './baseApi'
import type { Branch, Category, Review, Service, Staff } from '../types/storefront'

export interface ServicesQuery {
  slug: string
  categoryId?: number | null
  branchId?: number | null
  q?: string
  page?: number
  perPage?: number
}

/** What the pages actually consume: a flat page of services plus enough
 *  metadata to decide whether a "load more" button is still useful. */
export interface ServicePage {
  items: Service[]
  page: number
  lastPage: number
  total: number
}

interface RawPaginator {
  items?: unknown
  data?: unknown
  meta?: { current_page?: number; last_page?: number; total?: number }
  current_page?: number
  last_page?: number
  total?: number
}

/**
 * baseApi already unwraps Laravel's { status, message, data } envelope. What is
 * left is either a bare array (an unpaginated resource collection) or the
 * { items, meta } page the storefront API sends — see Paginated<T> in
 * types/storefront.ts, which pins that contract.
 *
 * This read `data` only and never `items`, so every service list in the app came
 * back empty while the endpoint was plainly returning rows. Laravel's own
 * `data` / inline-key paginator shapes are still accepted as tolerance.
 */
function toServicePage(raw: unknown, requestedPage: number): ServicePage {
  if (Array.isArray(raw)) {
    const items = raw as Service[]
    return { items, page: 1, lastPage: 1, total: items.length }
  }

  const paginator = (raw ?? {}) as RawPaginator

  // `items` is what the storefront API actually sends (see Paginated<T> in
  // types/storefront.ts). `data` is Laravel's own paginator shape, kept as
  // tolerance for any endpoint that returns one unwrapped.
  const items = Array.isArray(paginator.items)
    ? (paginator.items as Service[])
    : Array.isArray(paginator.data)
      ? (paginator.data as Service[])
      : []

  const meta = paginator.meta ?? paginator
  return {
    items,
    page: meta.current_page ?? requestedPage,
    lastPage: meta.last_page ?? 1,
    total: meta.total ?? items.length,
  }
}

function withParams(path: string, params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `${path}?${qs}` : path
}

export const catalogApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCategories: build.query<Category[], string>({
      query: (slug) => storefrontPath(slug, '/categories'),
      providesTags: ['Category'],
    }),

    getServices: build.query<ServicePage, ServicesQuery>({
      query: ({ slug, categoryId, branchId, q, page, perPage }) =>
        storefrontPath(
          slug,
          withParams('/services', {
            category_id: categoryId,
            branch_id: branchId,
            q,
            page,
            per_page: perPage,
          }),
        ),
      transformResponse: (raw: unknown, _meta, arg) => toServicePage(raw, arg.page ?? 1),
      providesTags: ['Service'],
    }),

    getService: build.query<Service, { slug: string; id: number }>({
      query: ({ slug, id }) => storefrontPath(slug, `/services/${id}`),
      providesTags: ['Service'],
    }),

    getBranches: build.query<Branch[], string>({
      query: (slug) => storefrontPath(slug, '/branches'),
      providesTags: ['Branch'],
    }),

    getStaff: build.query<Staff[], { slug: string; branchId?: number | null; serviceId?: number | null }>({
      query: ({ slug, branchId, serviceId }) =>
        storefrontPath(slug, withParams('/staff', { branch_id: branchId, service_id: serviceId })),
      providesTags: ['Staff'],
    }),

    getReviews: build.query<Review[], { slug: string; branchId?: number | null }>({
      query: ({ slug, branchId }) => storefrontPath(slug, withParams('/reviews', { branch_id: branchId })),
      // The reviews route may not be deployed yet; an empty list is the honest
      // answer for a 404 so the home page renders without a red box.
      transformResponse: (raw: unknown) => (Array.isArray(raw) ? (raw as Review[]) : []),
      providesTags: ['Review'],
    }),
  }),
})

export const {
  useGetCategoriesQuery,
  useGetServicesQuery,
  useGetServiceQuery,
  useGetBranchesQuery,
  useGetStaffQuery,
  useGetReviewsQuery,
} = catalogApi

/** True for the "endpoint is not there yet" case, which pages treat as empty. */
export function isMissingResource(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 404
}
